
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface Review {
  id: string;
  title: string;
  author: string;
  genre: string;
  rating: number;
  verdict: string;
  full_review: string;
  cover_image?: string;
  isbn?: string;
  publisher?: string;
  published_date?: string;
  status: 'published' | 'draft';
  created_at: string;
  updated_at: string;
}

export const reviewsService = {
  async getAllReviews(): Promise<Review[]> {
    const { data, error } = await supabase
      .from('book_reviews')
      .select('*')
      .eq('status', 'published')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching reviews:', error);
      throw error;
    }
    return data || [];
  },

  async getReviews(): Promise<Review[]> {
    const { data, error } = await supabase
      .from('book_reviews')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching all reviews:', error);
      throw error;
    }
    return data || [];
  },

  async getReviewById(id: string): Promise<Review | null> {
    const { data, error } = await supabase
      .from('book_reviews')
      .select('*')
      .eq('id', id)
      .eq('status', 'published')
      .single();
    
    if (error) {
      console.error('Error fetching review:', error);
      return null;
    }
    return data;
  },

  async createReview(review: Omit<Review, 'id' | 'created_at' | 'updated_at'>): Promise<Review> {
    const { data, error } = await supabase
      .from('book_reviews')
      .insert([review])
      .select()
      .single();
    
    if (error) {
      console.error('Error creating review:', error);
      throw error;
    }
    return data;
  },

  async updateReview(id: string, updates: Partial<Review>): Promise<Review> {
    const { created_at, updated_at, ...cleanUpdates } = updates;
    
    const { data, error } = await supabase
      .from('book_reviews')
      .update(cleanUpdates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating review:', error);
      throw error;
    }
    return data;
  },

  async deleteReview(id: string): Promise<void> {
    const { error } = await supabase
      .from('book_reviews')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  }
};
